import { CheckCircleIcon, XCircleIcon, ClockIcon } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { CreditTransaction } from '@/types/database.types';

interface CreditTransactionItemProps {
  transaction: CreditTransaction;
}

export default function CreditTransactionItem({ transaction }: CreditTransactionItemProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const getStatusIcon = () => {
    switch (transaction.status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 'failed':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getDescription = () => {
    if (transaction.type === 'earning' && transaction.snap_title) {
      return `Earnings from "${transaction.snap_title}"`;
    }
    return transaction.description || (transaction.type === 'withdrawal' ? 'Cashout request' : 'Unknown transaction');
  };

  return (
    <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              transaction.type === 'earning' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
            }`}>
              {transaction.type === 'earning' ? 'Earning' : 'Withdrawal'}
            </span>
            <span className="ml-2 text-sm text-gray-500">
              {formatDistanceToNow(new Date(transaction.created_at), { addSuffix: true })}
            </span>
          </div>
          <p className="mt-1 text-sm font-medium text-gray-900">{getDescription()}</p>
        </div>
        <div className="flex flex-col items-end">
          <div className="flex items-center">
            {getStatusIcon()}
            <span className="ml-1 text-sm text-gray-500 capitalize">{transaction.status}</span>
          </div>
          <p className={`mt-1 text-lg font-semibold ${
            transaction.type === 'earning' ? 'text-green-600' : 'text-blue-600'
          }`}>
            {transaction.type === 'earning' ? '+' : '-'}{formatCurrency(transaction.amount)}
          </p>
        </div>
      </div>
    </div>
  );
}